/**
 * «Productos» in «Análisis», besides what leaves the most and what stands still (the owner,
 * 2026-10-13, of what the page could add: «let's build all of it»): what each category sells and
 * leaves against what it holds on the shelf, what will take more than three months to sell, what
 * has run out and what each day without it goes unsold, and what a new price would do to what a
 * product leaves.
 *
 * Sales are counted line by line as profit by product counts them (`netosDeLineas`): the ticket's
 * discount spread, what came back taken off, voided sales nowhere. The shelf is what the shop holds
 * today, everywhere together, at the average it is carried at. Pure, so the screen and its tests
 * share one answer.
 */

import {
  costOf,
  diasEntre,
  margenBp,
  saleToBase,
  sumarDias,
  type Centavos,
  type Milli,
} from '@abastece/shared';
import { netosDeLineas, type Conteo, type Existencia, type Producto, type Reempaque, type Salida, type Venta } from '@abastece/core';
import type { Tramo } from './analisis.js';

/** The days a product's pace is measured over: three months, as «Mercadería parada» looks back. */
export const DIAS_DE_RITMO = 90;
/** Stock that lasts longer than this at the pace it sells is money on the shelf ahead of its time. */
export const DIAS_DE_SOBRA = 90;
/** What has not sold in this many days is «Mercadería parada», not slow. */
export const DIAS_VENDIDO = 30;
/** What ran out longer ago than this is no longer carried, as far as anyone can tell. */
export const DIAS_AGOTADO = 60;
/** The days before it ran out that say what a product was selling. */
export const DIAS_ANTES_DE_AGOTARSE = 28;
/** The least a pace is spread over: one busy first morning is not a pace. */
const DIAS_MINIMOS = 7;
/** «Al mes», in days. */
const MES = 30;

const nombreDeCategoria = (p: Producto): string => p.categoria.trim() || 'Otros';

/** What the shop holds of each product now, everywhere together, and what it cost. */
function estantePorProducto(existencias: readonly Existencia[]): Map<string, { cantidad: number; valor: number }> {
  const hay = new Map<string, { cantidad: number; valor: number }>();
  for (const e of existencias) {
    const x = hay.get(e.productoId) ?? { cantidad: 0, valor: 0 };
    x.cantidad += e.cantidad;
    if (e.cantidad > 0) x.valor += costOf(e.costoPromedio, e.cantidad);
    hay.set(e.productoId, x);
  }
  return hay;
}

export interface Salio {
  /** Base units, in milli. */
  base: number;
  ingreso: number;
  costo: number;
}

/** What each product's lines sold from `desde` to `hasta`, in base units, brought in and cost. */
export function vendidoPorProducto(ventas: readonly Venta[], desde: string, hasta: string): Map<string, Salio> {
  const salio = new Map<string, Salio>();
  for (const v of ventas) {
    if (v.anulada || v.fecha < desde || v.fecha > hasta) continue;
    const netos = netosDeLineas(v);
    for (const [i, l] of v.lineas.entries()) {
      if (!l.productoId) continue;
      const n = netos[i]!;
      const x = salio.get(l.productoId) ?? { base: 0, ingreso: 0, costo: 0 };
      x.base += saleToBase(n.cantidad, l.factor);
      x.ingreso += n.ingreso;
      x.costo += n.costo;
      salio.set(l.productoId, x);
    }
  }
  return salio;
}

/** The days a pace from `desde` to `hasta` is spread over: from the shop's first sale if later, a week at least. */
export const diasDeRitmo = (desde: string, hasta: string, primeraVenta: string | null): number =>
  Math.max(DIAS_MINIMOS, diasEntre(primeraVenta !== null && primeraVenta > desde ? primeraVenta : desde, hasta) + 1);

// ---------------------------------------------------------------------------
// Por categoría
// ---------------------------------------------------------------------------

export interface FilaCategoria {
  /** As the products carry it; «Otros» for none. */
  categoria: string;
  ingreso: Centavos;
  costo: Centavos;
  utilidad: Centavos;
  margenBp: number | null;
  /** What its products hold now, at what they cost. */
  estante: Centavos;
  /**
   * What it leaves a month for what it holds, in basis points: 4,500 is C$ 45 a month for each
   * C$ 100 on the shelf. Null with nothing on the shelf.
   */
  rindeBp: number | null;
}

/**
 * Each category's sales in the period and what they left, against what its products hold on the
 * shelf now: what the money in each category's goods earns a month. Categories that sold nothing
 * but hold something are there too, at the end: that is money earning nothing.
 */
export function porCategoria(d: {
  ventas: readonly Venta[];
  productos: readonly Producto[];
  existencias: readonly Existencia[];
  tramo: Tramo;
}): FilaCategoria[] {
  const categoriaDe = new Map(d.productos.map((p) => [p.id, nombreDeCategoria(p)]));
  const filas = new Map<string, { ingreso: number; costo: number; estante: number }>();
  const fila = (categoria: string) => {
    const f = filas.get(categoria) ?? { ingreso: 0, costo: 0, estante: 0 };
    filas.set(categoria, f);
    return f;
  };
  for (const [productoId, x] of vendidoPorProducto(d.ventas, d.tramo.desde, d.tramo.hasta)) {
    const f = fila(categoriaDe.get(productoId) ?? 'Otros');
    f.ingreso += x.ingreso;
    f.costo += x.costo;
  }
  for (const [productoId, x] of estantePorProducto(d.existencias)) {
    if (x.valor > 0) fila(categoriaDe.get(productoId) ?? 'Otros').estante += x.valor;
  }
  const dias = diasEntre(d.tramo.desde, d.tramo.hasta) + 1;
  return [...filas.entries()]
    .map(([categoria, f]) => {
      const utilidad = f.ingreso - f.costo;
      return {
        categoria,
        ingreso: f.ingreso as Centavos,
        costo: f.costo as Centavos,
        utilidad: utilidad as Centavos,
        margenBp: f.ingreso > 0 ? Math.round((utilidad * 10_000) / f.ingreso) : null,
        estante: f.estante as Centavos,
        rindeBp: f.estante > 0 ? Math.round((utilidad * MES * 10_000) / dias / f.estante) : null,
      };
    })
    .sort((a, b) => b.utilidad - a.utilidad || b.estante - a.estante || a.categoria.localeCompare(b.categoria, 'es'));
}

export interface ProductoDeCategoria {
  productoId: string;
  ingreso: Centavos;
  utilidad: Centavos;
  margenBp: number | null;
  estante: Centavos;
}

/** What makes up a category's row: each of its products that sold in the period or holds something, what leaves the most first. */
export function productosDeLaCategoria(d: {
  categoria: string;
  ventas: readonly Venta[];
  productos: readonly Producto[];
  existencias: readonly Existencia[];
  tramo: Tramo;
}): ProductoDeCategoria[] {
  const vendido = vendidoPorProducto(d.ventas, d.tramo.desde, d.tramo.hasta);
  const hay = estantePorProducto(d.existencias);
  const filas: ProductoDeCategoria[] = [];
  for (const p of d.productos) {
    if (nombreDeCategoria(p) !== d.categoria) continue;
    const x = vendido.get(p.id);
    const estante = hay.get(p.id)?.valor ?? 0;
    if (!x && estante <= 0) continue;
    const ingreso = x?.ingreso ?? 0;
    const utilidad = ingreso - (x?.costo ?? 0);
    filas.push({
      productoId: p.id,
      ingreso: ingreso as Centavos,
      utilidad: utilidad as Centavos,
      margenBp: ingreso > 0 ? Math.round((utilidad * 10_000) / ingreso) : null,
      estante: estante as Centavos,
    });
  }
  return filas.sort((a, b) => b.utilidad - a.utilidad || b.estante - a.estante);
}

// ---------------------------------------------------------------------------
// Mucho en el estante
// ---------------------------------------------------------------------------

/**
 * What leaves of each product a day, in base units, in milli: sold, and bagged into something
 * else, the last ninety days, from the shop's first sale if later, over a week at least.
 */
export function salidaPorDia(d: { ventas: readonly Venta[]; reempaques: readonly Reempaque[]; hoy: string; primeraVenta: string | null }): Map<string, number> {
  const desde = sumarDias(d.hoy, -(DIAS_DE_RITMO - 1));
  const salio = new Map([...vendidoPorProducto(d.ventas, desde, d.hoy)].map(([id, x]) => [id, x.base]));
  for (const r of d.reempaques) {
    if (r.fecha >= desde && r.fecha <= d.hoy) salio.set(r.origenProductoId, (salio.get(r.origenProductoId) ?? 0) + r.origenCantidad);
  }
  const dias = diasDeRitmo(desde, d.hoy, d.primeraVenta);
  return new Map([...salio].map(([id, base]) => [id, base / dias]));
}

export interface Sobrante {
  productoId: string;
  nombre: string;
  cantidad: Milli;
  unidad: string;
  /** What it cost, at the average it is carried at. */
  valor: Centavos;
  /** What leaves a day, in base units, in milli: sold, and bagged into something else. */
  porDia: number;
  /** How many days what it holds lasts at that pace. */
  dias: number;
}

/**
 * What sells, but holds more than three months of it: at what it sold and was bagged the last
 * ninety days (from the shop's first sale, if later), how long what is on the shelf lasts. What
 * has not sold in thirty days is «Mercadería parada» and is not here twice. The dearest first.
 */
export function muchoEnElEstante(d: {
  productos: readonly Producto[];
  existencias: readonly Existencia[];
  ventas: readonly Venta[];
  reempaques: readonly Reempaque[];
  hoy: string;
  primeraVenta: string | null;
}): Sobrante[] {
  const reciente = vendidoPorProducto(d.ventas, sumarDias(d.hoy, -(DIAS_VENDIDO - 1)), d.hoy);
  const ritmo = salidaPorDia(d);
  const hay = estantePorProducto(d.existencias);
  const sobrantes: Sobrante[] = [];
  for (const p of d.productos) {
    const x = hay.get(p.id);
    if (!p.activo || !x || x.cantidad <= 0 || !reciente.has(p.id)) continue;
    const porDia = ritmo.get(p.id) ?? 0;
    if (porDia <= 0) continue;
    const alcanza = x.cantidad / porDia;
    if (alcanza <= DIAS_DE_SOBRA) continue;
    sobrantes.push({ productoId: p.id, nombre: p.nombre, cantidad: x.cantidad as Milli, unidad: p.unidadBase, valor: x.valor as Centavos, porDia, dias: Math.round(alcanza) });
  }
  return sobrantes.sort((a, b) => b.valor - a.valor || a.nombre.localeCompare(b.nombre, 'es'));
}

// ---------------------------------------------------------------------------
// Agotados
// ---------------------------------------------------------------------------

export interface Agotado {
  productoId: string;
  nombre: string;
  /** The last day any of it left, sold, taken out, bagged or counted short: when it ran out, as near as the records say. */
  desde: string;
  /** Days since then; 0 if it ran out today. */
  dias: number;
  /** What it sold a day before it ran out, in base units, in milli. */
  porDia: number;
  /** What that brought in a day, and left. */
  ventaAlDia: Centavos;
  utilidadAlDia: Centavos;
  /** What went unsold since, at that pace. */
  perdido: Centavos;
}

/**
 * What the shop has none of and was selling: since the last day any of it left, and what it was
 * selling a day in the four weeks up to then (from the shop's first sale, if later; a week at
 * least), what each day without it goes unsold and what it would have left. What ran out more than
 * two months ago is no longer carried, as far as anyone can tell, and what never sold is not missed.
 * What each day costs first.
 */
export function agotados(d: {
  productos: readonly Producto[];
  existencias: readonly Existencia[];
  /** From `DIAS_AGOTADO + DIAS_ANTES_DE_AGOTARSE` days back at least. */
  ventas: readonly Venta[];
  salidas: readonly Salida[];
  reempaques: readonly Reempaque[];
  conteos: readonly Conteo[];
  hoy: string;
  primeraVenta: string | null;
}): Agotado[] {
  const hay = estantePorProducto(d.existencias);
  const ultima = new Map<string, string>();
  const salio = (productoId: string, fecha: string) => {
    if (fecha > d.hoy) return;
    const antes = ultima.get(productoId);
    if (!antes || fecha > antes) ultima.set(productoId, fecha);
  };
  for (const v of d.ventas) {
    if (v.anulada) continue;
    for (const l of v.lineas) if (l.productoId) salio(l.productoId, v.fecha);
  }
  for (const s of d.salidas) salio(s.productoId, s.fecha);
  for (const r of d.reempaques) salio(r.origenProductoId, r.fecha);
  // A count that found less than there should have been is when the rest was found gone.
  for (const c of d.conteos) for (const l of c.lineas) if (!l.superada && l.diferencia < 0) salio(l.productoId, c.fecha);

  const corte = sumarDias(d.hoy, -DIAS_AGOTADO);
  const lista: Agotado[] = [];
  for (const p of d.productos) {
    if (!p.activo || (hay.get(p.id)?.cantidad ?? 0) > 0) continue;
    const desde = ultima.get(p.id);
    if (!desde || desde < corte) continue;
    const inicio = sumarDias(desde, -(DIAS_ANTES_DE_AGOTARSE - 1));
    const x = vendidoPorProducto(d.ventas, inicio, desde).get(p.id);
    if (!x || x.ingreso <= 0) continue;
    const dias = diasDeRitmo(inicio, desde, d.primeraVenta);
    const ventaAlDia = x.ingreso / dias;
    const sinEl = diasEntre(desde, d.hoy);
    lista.push({
      productoId: p.id,
      nombre: p.nombre,
      desde,
      dias: sinEl,
      porDia: x.base / dias,
      ventaAlDia: Math.round(ventaAlDia) as Centavos,
      utilidadAlDia: Math.round((x.ingreso - x.costo) / dias) as Centavos,
      perdido: Math.round(ventaAlDia * sinEl) as Centavos,
    });
  }
  return lista.sort((a, b) => b.ventaAlDia - a.ventaAlDia || a.nombre.localeCompare(b.nombre, 'es'));
}

// ---------------------------------------------------------------------------
// Simular un precio
// ---------------------------------------------------------------------------

export interface APrecio {
  precio: Centavos;
  /** What one unit leaves over what it costs. */
  deja: Centavos;
  margenBp: number;
}

export interface Simulacion {
  antes: APrecio;
  nuevo: APrecio;
  /** Units of the presentation sold the last thirty days, in milli, and what they leave at today's price. */
  vendidas: number;
  dejan: Centavos;
  /**
   * The units the new price needs to sell to leave as much, in milli, rounded up to a whole unit:
   * fewer for a dearer price, more for a cheaper one. Null when nothing sold, or the new price
   * leaves nothing on a unit.
   */
  mismas: number | null;
}

/** What a unit leaves at a price, against what it costs. */
export const aPrecio = (precio: Centavos, costo: Centavos): APrecio => ({ precio, deja: (precio - costo) as Centavos, margenBp: margenBp(precio, costo) });

/**
 * A price tried before it is set: what each unit would leave against today's, and how many would
 * have to sell to leave what the last thirty days left. No guess at how many fewer buy at a higher
 * price: that is the owner's to judge, with the number to beat in front of them.
 */
export function simularPrecio(d: { precio: Centavos; costo: Centavos; nuevo: Centavos; vendidas: number }): Simulacion {
  const antes = aPrecio(d.precio, d.costo);
  const nuevo = aPrecio(d.nuevo, d.costo);
  const dejan = Math.round((d.vendidas * antes.deja) / 1_000);
  return {
    antes,
    nuevo,
    vendidas: d.vendidas,
    dejan: dejan as Centavos,
    mismas: d.vendidas > 0 && nuevo.deja > 0 && antes.deja > 0 ? Math.ceil((d.vendidas * antes.deja) / nuevo.deja / 1_000) * 1_000 : null,
  };
}

/** Units of a presentation sold from `desde` to `hasta`, all its product's presentations turned into it, in milli. */
export function vendidasDe(ventas: readonly Venta[], productoId: string, factor: Milli, desde: string, hasta: string): number {
  const base = vendidoPorProducto(ventas, desde, hasta).get(productoId)?.base ?? 0;
  return factor > 0 ? Math.round((base * 1_000) / factor) : 0;
}
